export function renderHelp(): string {
  return `
    <div class="help-page">
      <div class="container" style="max-width: 720px; padding: 60px 20px;">
        <h1 style="font-size: 2rem; font-weight: 700; color: #111827; margin-bottom: 8px;">Help &amp; Support</h1>
        <p style="color: #6b7280; font-size: 1.1rem; margin-bottom: 48px;">Get up and running with SideScribe, or find answers to common questions.</p>

        <div class="help-section">
          <h2>Getting started</h2>
          <ol class="help-steps">
            <li>
              <strong>Install SideScribe</strong> — Find SideScribe in the Microsoft 365 store (Insert → Add-ins → search "SideScribe") or install it from
              <a href="https://appsource.microsoft.com" target="_blank" rel="noopener">Microsoft AppSource</a>.
            </li>
            <li>
              <strong>Create an account</strong> — Sign up at <a href="#/signup">sidescribe.ai/signup</a> and verify your email address.
            </li>
            <li>
              <strong>Add an API key</strong> — Go to your <a href="#/dashboard">Dashboard</a> and enter at least one API key (OpenAI, Anthropic, or Google AI Studio). See <a href="#/getting-started">Getting Started</a> for step-by-step instructions on obtaining a key.
            </li>
            <li>
              <strong>Open the add-in in Word</strong> — Click the SideScribe button in the Word Home ribbon, sign in with your account, and you're ready.
            </li>
            <li>
              <strong>Run a review</strong> — Select text in your document, choose a review type, and click <em>Review</em>.
            </li>
          </ol>
        </div>

        <div class="help-section">
          <h2>Frequently asked questions</h2>

          <div class="faq-item">
            <h3>Why does the add-in show a "Setup required" screen?</h3>
            <p>This appears when your email address is not yet verified, or when you haven't saved an API key yet. Complete both steps in your <a href="#/dashboard">Dashboard</a> and the add-in will refresh automatically.</p>
          </div>

          <div class="faq-item">
            <h3>Which AI models does SideScribe support?</h3>
            <p>SideScribe supports models from three providers:</p>
            <ul>
              <li><strong>Anthropic</strong> — Claude Opus, Sonnet, and Haiku</li>
              <li><strong>OpenAI</strong> — GPT-4o, GPT-4.1, and other GPT models</li>
              <li><strong>Google</strong> — Gemini 2.5 Pro, Gemini 2.0 Flash, and Gemini 1.5 models</li>
            </ul>
            <p>You can switch between providers and models in the add-in settings.</p>
          </div>

          <div class="faq-item">
            <h3>Does SideScribe store my documents?</h3>
            <p>No. Text you submit for review is sent directly from your device to your chosen AI provider using your own API key. It never passes through SideScribe servers. See our <a href="#/privacy">Privacy Policy</a> for full details.</p>
          </div>

          <div class="faq-item">
            <h3>Where are my API keys stored?</h3>
            <p>API keys are stored in your secure Firestore user record, accessible only to your account. They are never logged or shared. You can update or delete them any time from your <a href="#/dashboard">Dashboard</a>.</p>
          </div>

          <div class="faq-item">
            <h3>How do I cancel my subscription?</h3>
            <p>Subscriptions are managed through Microsoft AppSource. To cancel, go to your Microsoft account at <a href="https://account.microsoft.com/services" target="_blank" rel="noopener">account.microsoft.com/services</a>, find SideScribe, and cancel. Your access continues until the end of the current billing period.</p>
          </div>

          <div class="faq-item">
            <h3>How do I save a custom prompt?</h3>
            <p>In the add-in, type your prompt in the prompt field, then click the save icon next to it. Give it a name and it will appear in your saved prompts list for one-click reuse.</p>
          </div>

          <div class="faq-item">
            <h3>The add-in isn't showing in my Word ribbon. What do I do?</h3>
            <ul>
              <li>Make sure you installed SideScribe from the Office Add-ins store (Insert → Add-ins).</li>
              <li>Try restarting Word.</li>
              <li>Check that your Microsoft 365 subscription supports add-ins (Microsoft 365 Personal, Family, or Business required).</li>
              <li>If the issue persists, try removing and reinstalling the add-in from Insert → Add-ins → My Add-ins.</li>
            </ul>
          </div>
        </div>

        <div class="help-contact">
          <h2>Still need help?</h2>
          <p>Email us at <a href="mailto:support@sidescribe.ai">support@sidescribe.ai</a> and we'll get back to you as soon as possible.</p>
        </div>
      </div>
    </div>
  `;
}

export function mountHelp(): void {
  // No dynamic behavior needed
}
